import { PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useState } from 'react';

type Conversation = {
  id: string;
  name: string;
  lastUpdatedDate?: string;
  startDate: string;
};

type Props = {
  pipelineExecutionState?: PipelineExecutionState;
  handleNewChat: () => void;
  conversations?: Conversation[];
  conversationsLoading?: boolean;
  onSelectConversation?: (conversationId: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
};

const ChantAssistantHeader = ({
  pipelineExecutionState,
  handleNewChat,
  conversations = [],
  conversationsLoading = false,
  onSelectConversation,
  onDeleteConversation,
}: Props) => {
  const confirm = useConfirm();
  const [historyAnchor, setHistoryAnchor] = useState<null | HTMLElement>(null);

  const handleOpenHistory = (e: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchor(e.currentTarget);
  };
  const handleCloseHistory = () => setHistoryAnchor(null);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SmartToyIcon sx={{ fontSize: 22, color: '#233565' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Minotii Assistant
        </Typography>
        <Chip
          label="AI Assited"
          size="small"
          color="primary"
          sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
        />
        {pipelineExecutionState?.pipelineName && (
          <Chip
            label={pipelineExecutionState.pipelineName}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center">
        {/* Conversation history button */}
        <Tooltip title="Conversation history">
          <IconButton size="small" onClick={handleOpenHistory} sx={{ color: '#64748b' }}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(historyAnchor)}
          anchorEl={historyAnchor}
          onClose={handleCloseHistory}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { width: 320, maxHeight: 400, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
          }}
        >
          <Stack sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Past Conversations
            </Typography>
          </Stack>
          <Divider />
          {conversationsLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              No saved conversations yet.
            </Typography>
          ) : (
            <List dense sx={{ overflow: 'auto', flexGrow: 1 }}>
              {conversations.map((conv) => (
                <ListItem
                  key={conv.id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await confirm({
                            title: 'Delete Conversation?',
                            description: `Are you sure you want to delete "${conv.name || 'Untitled'}" and all its messages from the database? This action cannot be undone.`,
                            confirmationText: 'Delete',
                            cancellationText: 'Cancel',
                            confirmationButtonProps: { color: 'error', variant: 'contained' },
                          });
                          await onDeleteConversation?.(conv.id);
                        } catch {
                          // User cancelled
                        }
                      }}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => {
                      onSelectConversation?.(conv.id);
                      handleCloseHistory();
                    }}
                    sx={{ pr: 6, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <ListItemText
                      primary={conv.name || 'Untitled'}
                      secondary={new Date(conv.lastUpdatedDate || conv.startDate).toLocaleString()}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Popover>

        <Button
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={handleNewChat}
          sx={{ fontSize: '0.72rem', textTransform: 'none', py: 0.25 }}
        >
          New Chat
        </Button>
      </Stack>
    </Stack>
  );
};

export default ChantAssistantHeader;
